/**
 * Game modes (docs/spellweave-modes.md §4 "Mode framework"). The host picks one
 * in the lobby; the room then plays that mode's level under its rules. Both
 * sides read this table: the server for the rules, the client for the
 * lobby picker and the HUD.
 */

import { INTERP_DELAY_MS, SNAP_EVERY } from './constants.js';

export type ModeId = 'waves' | 'story' | 'ctf';

export interface ModeDef {
  id: ModeId;
  /** Lobby button. */
  name: string;
  /** One line under it. */
  blurb: string;
  /** The level the mode plays (levels.ts). */
  level: string;
  /** Lightning on a body standing in water also hits everyone else in that water. */
  waterConducts: boolean;
  /**
   * Player against player (docs/spellweave-modes.md §2): two teams, damage between players scaled, Magick cooldowns,
   * respawns, rewound beams and arcs, snapshots every tick drawn closer behind. Absent in co-op modes.
   */
  pvp?: { teams: 2 };
  /** Server ticks between snapshots (SNAP_EVERY unless a mode sends more often). */
  snapEvery: number;
  /** How far behind the client draws other players, ms. */
  interpMs: number;
}

export const MODES: Readonly<Record<ModeId, ModeDef>> = {
  waves: { id: 'waves', name: 'Waves', blurb: 'Hold the arena against wave after wave.', level: 'arena', waterConducts: false, snapEvery: SNAP_EVERY, interpMs: INTERP_DELAY_MS },
  story: { id: 'story', name: 'Story', blurb: 'Act 1: The Drowned Cloister.', level: 'cloister', waterConducts: true, snapEvery: SNAP_EVERY, interpMs: INTERP_DELAY_MS },
  ctf: { id: 'ctf', name: 'Capture the Flag', blurb: '2v2 on Twin Fonts. Bring their flag home.', level: 'fonts', waterConducts: false, pvp: { teams: 2 }, snapEvery: 1, interpMs: 100 },
};

export const MODE_IDS: readonly ModeId[] = ['waves', 'story', 'ctf'];

/** Is this a player-against-player mode? */
export function isPvp(mode: ModeId): boolean {
  return MODES[mode].pvp !== undefined;
}

/** What a room plays until the host picks something else. */
export const DEFAULT_MODE: ModeId = 'waves';

export function isModeId(x: unknown): x is ModeId {
  return typeof x === 'string' && (MODE_IDS as readonly string[]).includes(x);
}
